/**
 * Tier/plan definitions for the onboarding flow
 */

import type { Plan, PublishedPlan } from "@headerhawk/core";
import { TEAM_TRIAL_DAYS, TIER_QUOTAS } from "@headerhawk/core/plans";

export type { Plan };

/** The Team plan's free trial, in days, as checkout grants it. */
export const TRIAL_DAYS = TEAM_TRIAL_DAYS;

export type BillingInterval = "monthly" | "annual";

export interface TierPricing {
  monthly: number;
  annual: number;
}

export interface TierOption {
  /**
   * `PublishedPlan`, not `Plan`: these cards are how a customer picks and buys
   * a plan, and Enterprise has no published price or published limits — its
   * figures in `TIER_QUOTAS` are a contract's floor (task 345).
   */
  id: PublishedPlan;
  name: string;
  price: TierPricing;
  features: {
    sites: number;
    monthlyReports: number;
    retention: number;
    users: number;
  };
  highlights: string[];
  popular?: boolean;
}

/**
 * Calculate annual savings (difference between 12x monthly and annual price)
 */
export function getAnnualSavings(tier: TierOption): number {
  return tier.price.monthly * 12 - tier.price.annual;
}

/**
 * A plan's limits, read from what the code enforces rather than restated
 * (task 278). Prices are written out below: what a plan costs is its Stripe
 * price, which no package in this repo holds.
 */
function featuresFor(plan: PublishedPlan): TierOption["features"] {
  const quota = TIER_QUOTAS[plan];
  return {
    sites: quota.siteLimit,
    monthlyReports: quota.monthlyReportLimit,
    retention: quota.retentionDays,
    users: quota.userLimit,
  };
}

export const TIER_OPTIONS: TierOption[] = [
  {
    id: "free",
    name: "Free",
    price: { monthly: 0, annual: 0 },
    features: featuresFor("free"),
    highlights: [
      "Perfect for side projects",
      "Basic CSP monitoring",
      `${TIER_QUOTAS.free.retentionDays}-day data retention`,
    ],
  },
  {
    id: "team",
    name: "Team",
    price: { monthly: 39, annual: 390 },
    features: featuresFor("team"),
    highlights: [
      "For small teams",
      "Collaborative monitoring",
      `${TIER_QUOTAS.team.retentionDays}-day retention`,
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: { monthly: 99, annual: 990 },
    features: featuresFor("professional"),
    highlights: [
      "For growing businesses",
      "Extended retention",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: { monthly: 149, annual: 1490 },
    features: featuresFor("business"),
    highlights: [
      "For large organizations",
      "Maximum capacity",
      "Premium support",
    ],
  },
];
